import i18next from 'i18next'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { Link } from 'widgets-for-react'
import { HeadingLayout } from '../../../../utils/headings'
import { blockInviewHandler } from '../../../../utils/inview'
import { homeInViewThreshold } from '../../home/loggedOutHome/loggedOutHomeWidget'
import { PictureLayout } from '../../image/imageLayout'
import { RootLayout } from '../../rootLayout'
import { BlogTeaserData, BlogTeasersData } from './blogTeasersState'

export type BlogTeaserProps = {
  orientation: 'vertical' | 'horizontal'
  heading: 'h2' | 'h3'
  teaser: BlogTeaserData
  extraClasses?: string
}

export const BlogTeaserLayout = (p: BlogTeaserProps) => {
  const {
    Id,
    Path,
    HeaderDescription,
    HeaderTitle,
    HeaderImage,
    PublicationDateBegin,
  } = p.teaser
  const date = !isNaN(Date.parse(PublicationDateBegin))
    ? new Date(PublicationDateBegin)
    : undefined
  return (
    <li
      key={Id}
      className={`teaser--blog teaser--${p.orientation} ${
        p.extraClasses || ''
      }`}
    >
      <Link
        to={`${
          i18next.language == 'en' ? '/en/blog' : '/nl/blog'
        }${Path.toLowerCase().replace('%', '')}`}
        className="teaser__link"
      >
        <article className="teaser__inner">
          <div className="teaser__content">
            <header className="teaser__header">
              <HeadingLayout
                kind={p.heading}
                extraClasses={`teaser__title ${
                  p.orientation === 'horizontal'
                    ? 'title--xs-to-s'
                    : 'title--xs'
                }`}
                title={HeaderTitle}
              />
            </header>

            {p.orientation === 'horizontal' && date && (
              <time
                dateTime={date.toISOString()}
                className="teaser__date text--xs"
              >
                {date.toLocaleDateString(i18next.language, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </time>
            )}

            <p className="teaser__intro">{HeaderDescription}</p>
            <footer className="teaser__footer">
              <span className="teaser__fauxlink">
                {i18next.t('common:readMore')}
              </span>
            </footer>
          </div>

          <div className="teaser__visual fit-object">
            <PictureLayout.Img
              image={HeaderImage}
              width="400"
              height="300"
              extraClasses="teaser__image"
              lazyload
            />
          </div>
        </article>
      </Link>
    </li>
  )
}

export type PageType =
  | 'home-logged-out'
  | 'home-logged-in'
  | 'overview-page'
  | 'search-results'

type BlogTeasersLayoutProps = {
  kind: PageType
  title?: string
  blogTeasers: BlogTeasersData | 'loading' | 'error'
}

export const BlogTeasersLayout = (p: BlogTeasersLayoutProps) => {
  const isHome = p.kind === 'home-logged-out' || p.kind === 'home-logged-in'
  const isPublicHome = p.kind === 'home-logged-out'
  const isProHome = p.kind === 'home-logged-in'
  const isOverviewPage = p.kind === 'overview-page'
  const isSearchResult = p.kind === 'search-results'
  return (
    <InView
      as="section"
      threshold={p.kind == 'home-logged-out' ? homeInViewThreshold : 0}
      triggerOnce={true}
      onChange={(isInView, entry) => blockInviewHandler(isInView, entry.target)}
      className={`block block--blog-teasers blog-teasers--${
        isHome ? 'home' : isOverviewPage ? 'overview' : 'search-results'
      }${isHome ? ` blog-teasers--${p.kind}` : ''}`}
    >
      <div
        className={`block__inner${
          !isSearchResult && !isProHome ? ' container--m gutter--m' : ''
        }`}
      >
        {!isOverviewPage && (
          <header
            className={`block__header${
              isSearchResult
                ? ' block__header--search'
                : isPublicHome
                ? ' sr-text'
                : ''
            }`}
          >
            <h2
              className="block__title title--xs"
              dangerouslySetInnerHTML={{ __html: p.title || '' }}
            />
          </header>
        )}
        {p.blogTeasers === 'loading' ? (
          <RootLayout.Loader hasWrapper={true} showText={true} />
        ) : p.blogTeasers == 'error' ? (
          <RootLayout.LoadingFailed
            text={i18next.t('common:loadingFailed', {
              entity: i18next.t('blog:blogsTitle', { count: 2 }).toLowerCase(),
            })}
          />
        ) : p.blogTeasers.length > 0 ? (
          <div className="block__content">
            <ul className="block__list">
              {p.blogTeasers.map((teaser) => (
                <BlogTeaserLayout
                  key={teaser.Id}
                  orientation={isHome ? 'vertical' : 'horizontal'}
                  heading={isHome ? 'h3' : 'h2'}
                  extraClasses="block__item"
                  teaser={teaser}
                />
              ))}
            </ul>
          </div>
        ) : (
          <RootLayout.EmptyState
            text={i18next.t('common:emptyState', {
              entity: i18next.t('blog:blogsTitle', { count: 2 }).toLowerCase(),
            })}
          />
        )}
      </div>
    </InView>
  )
}
